import React, { useState } from "react";
import {
  Modal,
  Button,
  Table,
  NumberInput,
  Flex,
  Text,
  Box,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

interface DiscountSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSelectDiscount: (discount: number) => void;
  discountOptions?: Array<{
    id: number;
    label: string;
    value: number;
  }>;
  initialCustomDiscount?: number;
}

const DiscountSelectionModal: React.FC<DiscountSelectionModalProps> = ({
  opened,
  onClose,
  onSelectDiscount,
  discountOptions,
  initialCustomDiscount = 0,
}) => {
  const [customDiscount, setCustomDiscount] = useState<number>(0);

  // Debug: Log discountOptions khi modal mở
  React.useEffect(() => {
    if (opened) {
      console.log("DiscountSelectionModal - discountOptions:", discountOptions);
      // Set giá trị ban đầu khi modal mở
      setCustomDiscount(initialCustomDiscount);
    }
  }, [opened, discountOptions, initialCustomDiscount]);

  // Reset customDiscount khi modal đóng
  React.useEffect(() => {
    if (!opened) {
      setCustomDiscount(0);
    }
  }, [opened]);

  const handleSelectDiscount = (discount: number) => {
    onSelectDiscount(discount);
    onClose();
  };

  const handleCustomDiscountSelect = () => {
    if (customDiscount >= 0) {
      onSelectDiscount(customDiscount);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text size="lg" fw={600}>
          Chọn mức giảm giá
        </Text>
      }
      size="md"
      centered
    >
      <Box>
        {/* Bảng các mức giảm giá có sẵn */}
        <Text fw={500} mb="sm">
          Mức giảm giá có sẵn:
        </Text>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Loại giảm giá</Table.Th>
              <Table.Th>Số tiền</Table.Th>
              <Table.Th>Thao tác</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {discountOptions && discountOptions.length > 0 ? (
              discountOptions.map((option) => (
                <Table.Tr key={option.id}>
                  <Table.Td>{option.label}</Table.Td>
                  <Table.Td>
                    <Text fw={500} c="red">
                      -{option.value.toLocaleString("vi-VN")} VNĐ
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Chọn mức giảm giá này">
                      <ActionIcon
                        color="green"
                        variant="outline"
                        onClick={() => handleSelectDiscount(option.value)}
                      >
                        <IconCheck size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td
                  colSpan={3}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  <Text c="dimmed">Không có dữ liệu giảm giá</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        {/* Ô nhập giá trị tùy chỉnh */}
        <Box mt="lg">
          <Text fw={500} mb="sm">
            Nhập mức giảm giá khác:
          </Text>
          <Flex gap="sm" align="end">
            <NumberInput
              label="Số tiền giảm (VNĐ)"
              placeholder="Nhập số tiền muốn giảm"
              value={customDiscount}
              onChange={(value) => setCustomDiscount(Number(value) || 0)}
              thousandSeparator="."
              decimalSeparator=","
              min={0}
              hideControls
              style={{ flex: 1 }}
            />
            <Button
              color="blue"
              onClick={handleCustomDiscountSelect}
              disabled={customDiscount < 0}
            >
              Chọn
            </Button>
          </Flex>
        </Box>

        {/* Nút đóng */}
        <Flex justify="end" mt="lg">
          <Button variant="light" onClick={onClose}>
            Đóng
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default DiscountSelectionModal;
